import { Box, Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { FontFamily } from '@common/theming/Fonts';
import currency from '@common/utility/currency';
import { formatDate, getMonthsFirstDay } from '@common/utility/datetime';
import { MSReward, MSRewardTotals, MSRewards } from '@services/appsync/types';
import React, { useMemo } from 'react';
import { CURRENCY_OPTIONS } from '../../constants';
import { useStatementViewModel } from '../../models/StatementViewModel';

export const Rewards: React.FC = () => {
  const statementViewModel = useStatementViewModel();

  const rewards: MSRewards = useMemo(() => {
    const rewards = statementViewModel.statement?.activity?.rewards;

    const items: MSReward[] =
      rewards?.items?.map((reward) => ({
        __typename: 'MSReward',
        amount: reward.amount ? currency.format(reward.amount, CURRENCY_OPTIONS) : undefined,
        date: formatDate(new Date(`${reward.date}T00:00:00`), 'MMMM DD, YYYY'),
        description: reward.description,
        end_balance: currency.format(reward.end_balance, CURRENCY_OPTIONS),
      })) ?? [];

    const totals: MSRewardTotals = {
      __typename: 'MSRewardTotals',
      end_balance: rewards?.totals.end_balance ? currency.format(rewards.totals.end_balance, CURRENCY_OPTIONS) : '',
      start_balance: rewards?.totals.start_balance
        ? currency.format(rewards.totals.start_balance, CURRENCY_OPTIONS)
        : '',
    };

    items.unshift({
      __typename: 'MSReward',
      amount: undefined,
      date: getMonthsFirstDay(`${statementViewModel.month}-01`, 'MMMM DD, YYYY') ?? '',
      description: 'Rewards starting balance',
      end_balance: totals.start_balance,
    });
    return { __typename: 'MSRewards', items, totals };
  }, [statementViewModel.statement?.activity.rewards]);
  if (!rewards) return null;

  return (
    <Box w="full">
      <TableContainer>
        <Table px={'0px'} layout="fixed">
          <Thead>
            <Tr>
              <Td
                pl={'0px'}
                borderBottomStyle="none"
                fontSize="16px"
                fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                fontWeight="normal"
                colSpan={6}>
                Rewards
              </Td>
            </Tr>
            <Tr py="6px" borderBottom={'2px solid black'}>
              <Th
                className="statement-table-header"
                lineHeight={1}
                fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                fontWeight="normal"
                pl={'0px'}
                color="black">
                Date
              </Th>
              <Th
                className="statement-table-header"
                colSpan={3}
                lineHeight={1}
                fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                fontWeight="normal"
                color="black">
                Description
              </Th>
              <Th
                className="statement-table-header"
                lineHeight={1}
                fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                fontWeight="normal"
                color="black">
                Amount
              </Th>
              <Th
                className="statement-table-header"
                lineHeight={1}
                fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                fontWeight="normal"
                color="black">
                Balance
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {rewards.items?.map((item, i) => (
              <Tr py="6px" key={`holding-${i}`}>
                <Td pl="4px">{item.date}</Td>
                <Td colSpan={3}>{item.description}</Td>
                <Td>{item.amount}</Td>
                <Td>{item.end_balance}</Td>
              </Tr>
            ))}
            <Tr py="6px" backgroundColor="gray.50">
              <Td pl="4px" colSpan={5} fontFamily={FontFamily.HELVETICA_NOW_BOLD}>
                Ending Balance
              </Td>
              <Td fontFamily={FontFamily.HELVETICA_NOW_BOLD}>{rewards.totals.end_balance}</Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
